import type { RoyalChallengeHallTrialConfig } from './RoyalChallengeHallConfig.js';

export type RoyalChallengeHallAdjustResult = 'accepted' | 'limit' | 'complete';
export type RoyalChallengeHallCheckResult = 'complete' | 'too-low' | 'too-high';

export interface RoyalChallengeHallSerializedState {
  readonly version: 1;
  readonly value: number;
  readonly stepIndex: number;
  readonly hintStage: number;
  readonly attempts: number;
  readonly complete: boolean;
}

export interface RoyalChallengeHallSnapshot extends RoyalChallengeHallSerializedState {
  readonly stepSize: number;
  readonly difference: number;
  readonly hint?: string;
}

export class RoyalChallengeHallState {
  private value: number;
  private stepIndex = 0;
  private hintStage = 0;
  private attempts = 0;
  private completed = false;

  constructor(
    readonly config: RoyalChallengeHallTrialConfig,
    saved?: Partial<RoyalChallengeHallSerializedState>,
  ) {
    this.value = config.initialValue;
    if (saved?.version === 1) this.restore(saved);
  }

  adjust(direction: -1 | 1): RoyalChallengeHallAdjustResult {
    if (this.completed) return 'complete';
    const next = this.value + direction * this.snapshot.stepSize;
    if (next < this.config.minimum || next > this.config.maximum) return 'limit';
    this.value = next;
    return 'accepted';
  }

  cycleStep() {
    if (this.completed) return 'complete' as const;
    this.stepIndex = (this.stepIndex + 1) % this.config.stepSizes.length;
    return 'accepted' as const;
  }

  check(): RoyalChallengeHallCheckResult {
    this.attempts += 1;
    if (this.value < this.config.answer) return 'too-low';
    if (this.value > this.config.answer) return 'too-high';
    this.completed = true;
    return 'complete';
  }

  requestHint() {
    this.hintStage = Math.min(this.config.hints.length, this.hintStage + 1);
    return this.snapshot.hint;
  }

  serialize(): RoyalChallengeHallSerializedState {
    return {
      version: 1,
      value: this.value,
      stepIndex: this.stepIndex,
      hintStage: this.hintStage,
      attempts: this.attempts,
      complete: this.completed,
    };
  }

  get snapshot(): RoyalChallengeHallSnapshot {
    return {
      ...this.serialize(),
      stepSize: this.config.stepSizes[this.stepIndex],
      difference: this.config.answer - this.value,
      hint: this.hintStage > 0 ? this.config.hints[this.hintStage - 1] : undefined,
    };
  }

  private restore(saved: Partial<RoyalChallengeHallSerializedState>) {
    if (!Number.isInteger(saved.value)
      || Number(saved.value) < this.config.minimum
      || Number(saved.value) > this.config.maximum) return;
    this.value = Number(saved.value);
    this.stepIndex = Math.min(
      this.config.stepSizes.length - 1,
      Math.max(0, Number.isInteger(saved.stepIndex) ? Number(saved.stepIndex) : 0),
    );
    this.hintStage = Math.min(this.config.hints.length, Math.max(0, Number(saved.hintStage) || 0));
    this.attempts = Math.max(0, Number(saved.attempts) || 0);
    this.completed = saved.complete === true && this.value === this.config.answer;
  }
}
